/**
 * PhotoAnchorView — Split-screen anchor placement for Tier 3 photo registration
 *
 * Displays a thermal photo on the left (pannable, zoomable) and the map on the
 * right. For each anchor candidate photo, the user clicks on the map to indicate
 * where the photo center should actually be. This creates a GPS anchor pair
 * (original → corrected), and after 2+ anchors a rigid transform can be computed.
 *
 * Flow:
 *   1. Component shows first candidate photo on the left
 *   2. User views the photo to identify the area, then clicks on the map
 *   3. Anchor pair recorded (originalLng/Lat → clickedLng/Lat)
 *   4. Advance to next candidate (or user can skip/finish)
 *   5. After ≥2 anchors, "Done" returns anchors to the wizard
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMapStore, useUIStore } from '../../stores';
import { resolveImageSrc } from '../../utils/photoBlobs';
import { resolveSignedUrl } from '../../services/signedAssetUrl';
import type { RegistrationAnchor, AnchorCandidate, PhotoInput } from '../../services/photoRegistration';
import '../layout/LayoutAnchorMode.css';

interface Props {
  candidates: AnchorCandidate[];
  photos: PhotoInput[];
  onDone: (anchors: RegistrationAnchor[]) => void;
  onCancel: () => void;
}

export default function PhotoAnchorView({ candidates, photos, onDone, onCancel }: Props) {
  const addNotification = useUIStore((s) => s.addNotification);
  const setPhotoAnchorMode = useUIStore((s) => s.setPhotoAnchorMode);

  // Current candidate index
  const [currentIdx, setCurrentIdx] = useState(0);
  const [anchors, setAnchors] = useState<RegistrationAnchor[]>([]);
  const [waitingForMapClick, setWaitingForMapClick] = useState(true);

  // Image viewer state
  const canvasRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  const currentCandidate = candidates[currentIdx] ?? null;
  const currentPhoto = useMemo(
    () => currentCandidate ? photos.find((p) => p.id === currentCandidate.photoId) : null,
    [currentCandidate, photos],
  );

  // ── Enable photoAnchorMode so MapCanvas dispatches click events ──
  useEffect(() => {
    setPhotoAnchorMode(true);
    return () => setPhotoAnchorMode(false);
  }, [setPhotoAnchorMode]);

  // ── Load photo image ──
  // resolveImageSrc returns a blob: URL for in-session photos; for published
  // photos it falls through to the raw /api/v2/assets/ URL — that path needs
  // signing because Image() with crossOrigin='anonymous' strips the cookie.
  // resolveSignedUrl is a no-op for blob:/data: URLs.
  useEffect(() => {
    if (!currentPhoto) return;
    let revoked = false;
    (async () => {
      const url = resolveImageSrc(currentPhoto.url);
      const signedUrl = await resolveSignedUrl(url);
      if (revoked) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (revoked) return;
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        setDisplayUrl(signedUrl);
      };
      img.onerror = () => {
        if (revoked) return;
        setDisplayUrl(signedUrl);
      };
      img.src = signedUrl;
    })();
    return () => { revoked = true; };
  }, [currentPhoto?.url]);

  // ── Fit image on load ──
  useEffect(() => {
    if (!canvasRef.current || !imageSize || !displayUrl) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / imageSize.width;
    const scaleY = rect.height / imageSize.height;
    const fitZoom = Math.min(scaleX, scaleY) * 0.95;
    setZoom(fitZoom);
    setPan({
      x: (rect.width - imageSize.width * fitZoom) / 2,
      y: (rect.height - imageSize.height * fitZoom) / 2,
    });
  }, [imageSize, displayUrl]);

  // ── Center map on current photo's GPS ──
  useEffect(() => {
    if (!currentPhoto) return;
    const map = useMapStore.getState().map;
    if (map) {
      map.flyTo({ center: [currentPhoto.lng, currentPhoto.lat], zoom: 19, duration: 800 });
    }
    setWaitingForMapClick(true);
  }, [currentPhoto?.id]);

  // ── Register map click handler ──
  useEffect(() => {
    if (!waitingForMapClick || !currentPhoto) return;

    const handleMapClick = (e: CustomEvent<{ lng: number; lat: number }>) => {
      const newAnchor: RegistrationAnchor = {
        photoId: currentPhoto.id,
        originalLng: currentPhoto.lng,
        originalLat: currentPhoto.lat,
        correctedLng: e.detail.lng,
        correctedLat: e.detail.lat,
      };

      const updated = [...anchors, newAnchor];
      setAnchors(updated);
      setWaitingForMapClick(false);

      showAnchorMarker(newAnchor, updated.length);

      addNotification({
        type: 'success',
        title: `Anchor ${updated.length} placed`,
        message: updated.length >= 2
          ? `${updated.length} anchors — ready to compute transform`
          : 'Need at least 2 anchors',
        duration: 3000,
      });

      // Auto-advance to next candidate after short delay
      if (currentIdx < candidates.length - 1) {
        setTimeout(() => {
          setCurrentIdx((i) => i + 1);
          resetImageView();
        }, 500);
      }
    };

    window.addEventListener('layout-anchor-map-click', handleMapClick as EventListener);
    return () => window.removeEventListener('layout-anchor-map-click', handleMapClick as EventListener);
  }, [waitingForMapClick, currentPhoto, anchors, currentIdx, candidates.length, addNotification]);

  // ── Pan/zoom handlers ──
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    isDraggingRef.current = false;

    const handleMove = (me: MouseEvent) => {
      const dx = me.clientX - dragStartRef.current.x;
      const dy = me.clientY - dragStartRef.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) isDraggingRef.current = true;
      setPan({ x: dragStartRef.current.panX + dx, y: dragStartRef.current.panY + dy });
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      setTimeout(() => isDraggingRef.current = false, 10);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [pan]);

  // Native wheel for zoom (non-passive)
  const wheelStateRef = useRef({ zoom, pan });
  wheelStateRef.current = { zoom, pan };

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const { zoom: z, pan: p } = wheelStateRef.current;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.min(Math.max(z * factor, 0.05), 10);
      setPan({
        x: mouseX - (mouseX - p.x) * (newZoom / z),
        y: mouseY - (mouseY - p.y) * (newZoom / z),
      });
      setZoom(newZoom);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Helpers ──

  const resetImageView = () => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
    setImageSize(null);
    setDisplayUrl(null);
  };

  const handleSkip = () => {
    if (currentIdx < candidates.length - 1) {
      setCurrentIdx((i) => i + 1);
      resetImageView();
    }
  };

  const handleDeleteAnchor = (idx: number) => {
    setAnchors((prev) => prev.filter((_, i) => i !== idx));
    clearAllAnchorMarkers();
    // Re-render remaining markers
    const remaining = anchors.filter((_, i) => i !== idx);
    remaining.forEach((a, i) => showAnchorMarker(a, i + 1));
  };

  const handleDone = () => {
    clearAllAnchorMarkers();
    onDone(anchors);
  };

  const handleCancelClick = () => {
    clearAllAnchorMarkers();
    onCancel();
  };

  // ── Map anchor markers ──

  const showAnchorMarker = (anchor: RegistrationAnchor, num: number) => {
    const map = useMapStore.getState().map;
    if (!map) return;

    const sourceId = `photo-anchor-${num}`;
    const layerLineId = `photo-anchor-line-${num}`;
    const layerPointId = `photo-anchor-point-${num}`;

    try {
      if (map.getLayer(layerLineId)) map.removeLayer(layerLineId);
      if (map.getLayer(layerPointId)) map.removeLayer(layerPointId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch { /* */ }

    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [anchor.originalLng, anchor.originalLat],
                [anchor.correctedLng, anchor.correctedLat],
              ],
            },
          },
          {
            type: 'Feature',
            properties: { label: `${num}` },
            geometry: {
              type: 'Point',
              coordinates: [anchor.correctedLng, anchor.correctedLat],
            },
          },
        ],
      },
    });

    map.addLayer({
      id: layerLineId,
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': '#f97316',
        'line-width': 2,
        'line-dasharray': [4, 2],
      },
    });

    map.addLayer({
      id: layerPointId,
      type: 'circle',
      source: sourceId,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 8,
        'circle-color': '#22c55e',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
      },
    });
  };

  const removeAnchorMarker = (num: number) => {
    const map = useMapStore.getState().map;
    if (!map) return;
    try {
      if (map.getLayer(`photo-anchor-line-${num}`)) map.removeLayer(`photo-anchor-line-${num}`);
      if (map.getLayer(`photo-anchor-point-${num}`)) map.removeLayer(`photo-anchor-point-${num}`);
      if (map.getSource(`photo-anchor-${num}`)) map.removeSource(`photo-anchor-${num}`);
    } catch { /* */ }
  };

  const clearAllAnchorMarkers = () => {
    for (let i = 1; i <= anchors.length + 5; i++) {
      removeAnchorMarker(i);
    }
  };

  // ── Render ──

  if (!currentCandidate || !currentPhoto) {
    return createPortal(
      <div className="anchor-mode" style={{ position: 'fixed', inset: 0, zIndex: 9000 }}>
        <div className="anchor-mode__bar">
          <div className="anchor-mode__instructions">No anchor candidates available.</div>
          <button className="anchor-mode__done" onClick={handleCancelClick} type="button">Close</button>
        </div>
      </div>,
      document.body,
    );
  }

  const content = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '50vw',
      height: '100vh',
      zIndex: 9000,
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--sl-bg-primary)',
    }}>
      {/* Control bar spanning left pane */}
      <div className="anchor-mode__bar">
        <div className="anchor-mode__instructions">
          {waitingForMapClick
            ? `Photo ${currentIdx + 1}/${candidates.length}: View the photo, then click its correct position on the map →`
            : 'Anchor placed! Advancing...'}
        </div>
        <div className="anchor-mode__info">
          <span className="anchor-mode__count">{anchors.length} anchors</span>
          {anchors.length >= 2 && (
            <span className="anchor-mode__rmse anchor-mode__rmse--good">Ready</span>
          )}
        </div>
        <button
          className="anchor-mode__done"
          onClick={handleSkip}
          type="button"
          style={{ background: 'var(--sl-bg-subtle)', color: 'var(--sl-text-secondary)' }}
          disabled={currentIdx >= candidates.length - 1}
        >
          Skip
        </button>
        <button
          className="anchor-mode__done"
          onClick={handleDone}
          type="button"
          disabled={anchors.length < 2}
          title={anchors.length < 2 ? 'Need at least 2 anchors' : undefined}
        >
          Done ({anchors.length})
        </button>
        <button
          className="anchor-mode__done"
          onClick={handleCancelClick}
          type="button"
          style={{ background: 'var(--sl-bg-subtle)', color: 'var(--sl-text-secondary)' }}
        >
          Cancel
        </button>
      </div>

      {/* Photo viewer */}
      <div
        ref={canvasRef}
        className="anchor-mode__pdf"
        onMouseDown={handleMouseDown}
        style={{ flex: 1 }}
      >
        <div
          className="anchor-mode__pdf-content"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {displayUrl && (
            <img
              src={displayUrl}
              alt={currentPhoto.name}
              draggable={false}
              style={{ display: 'block' }}
            />
          )}
        </div>

        {/* Photo info overlay */}
        <div style={{
          position: 'absolute', top: 8, left: 8, padding: '4px 10px',
          background: 'rgba(0,0,0,0.7)', borderRadius: 6, color: '#fff',
          fontSize: 12, lineHeight: 1.5, pointerEvents: 'none',
        }}>
          <div style={{ fontWeight: 600 }}>{currentPhoto.name}</div>
          {currentPhoto.altitude != null && (
            <div>Alt: {currentPhoto.altitude.toFixed(1)}m</div>
          )}
          <div style={{ opacity: 0.7 }}>
            GPS: {currentPhoto.lat.toFixed(6)}, {currentPhoto.lng.toFixed(6)}
          </div>
          {currentCandidate.reason && (
            <div style={{ opacity: 0.7, fontStyle: 'italic' }}>{currentCandidate.reason}</div>
          )}
        </div>

        {/* Map-click prompt */}
        {waitingForMapClick && (
          <div className="anchor-mode__prompt">
            Click on the map where this photo should be →
          </div>
        )}
      </div>

      {/* Anchor list */}
      <div className="anchor-mode__list" style={{ maxHeight: 150 }}>
        <div className="anchor-mode__list-header">Anchors ({anchors.length})</div>
        {anchors.length === 0 && (
          <div className="anchor-mode__list-empty">
            No anchors yet. View each photo and click its correct position on the map.
          </div>
        )}
        {anchors.map((a, i) => {
          const photo = photos.find((p) => p.id === a.photoId);
          const dlat = (a.correctedLat - a.originalLat) * 111320;
          const dlng = (a.correctedLng - a.originalLng) * 111320 * Math.cos(a.originalLat * Math.PI / 180);
          const dist = Math.sqrt(dlat * dlat + dlng * dlng);
          return (
            <div key={i} className="anchor-mode__list-item">
              <span className="anchor-mode__list-num">{i + 1}</span>
              <span className="anchor-mode__list-coords" style={{ flex: 2 }}>
                {photo?.name ?? a.photoId}
              </span>
              <span className="anchor-mode__list-coords">
                {dist.toFixed(1)}m shift
              </span>
              <button
                className="anchor-mode__list-delete"
                onClick={() => handleDeleteAnchor(i)}
                type="button"
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
